import Axios, { AxiosError, AxiosHeaders, AxiosRequestConfig } from "axios";

import { appConfig, clearAccessToken, getAccessToken } from "@/config";
import { getLanguage } from "@/i18n";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  (Object.getPrototypeOf(value) === Object.prototype ||
    Object.getPrototypeOf(value) === null);

// Serializes query params the way the components expect: arrays repeat the key,
// nested objects use `key[sub]`, null/undefined are dropped.
const appendParam = (
  urlParams: URLSearchParams,
  key: string,
  value: unknown,
): void => {
  if (value === undefined || value === null) return;
  if (Array.isArray(value)) {
    value.forEach((item) => appendParam(urlParams, key, item));
    return;
  }
  if (isPlainObject(value)) {
    for (const subKey of Object.keys(value)) {
      appendParam(urlParams, `${key}[${subKey}]`, value[subKey]);
    }
    return;
  }
  urlParams.append(key, String(value));
};

export const AXIOS_INSTANCE = Axios.create({
  baseURL: appConfig.apiBaseUrl,
  // Bearer-token auth, no cookies — and the API's CORS runs with
  // credentials:false, so a credentialed cross-origin request would be
  // BLOCKED by the browser (no Access-Control-Allow-Credentials header).
  withCredentials: false,
  paramsSerializer: {
    serialize: (params) => {
      const urlParams = new URLSearchParams();
      for (const key in params) {
        appendParam(urlParams, key, params[key]);
      }
      return urlParams.toString();
    },
  },
});

// A 401 means the JWT is missing/expired: drop it and go back to login —
// unless we are already on a public auth screen.
AXIOS_INSTANCE.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401 && typeof window !== "undefined") {
      const path = window.location.pathname;
      const onPublicRoute =
        path.startsWith("/login") || path.startsWith("/auth/callback");
      if (!onPublicRoute) {
        clearAccessToken();
        window.location.assign("/login");
      }
    }
    return Promise.reject(error);
  },
);

export type ErrorType<Error> = AxiosError<Error>;
export type BodyType<BodyData> = BodyData;

type HeaderSource = Parameters<typeof AxiosHeaders.concat>[0];

// Single request mutator used by every API module. Returns the response body.
export const api = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const requestConfig: AxiosRequestConfig = {
    ...config,
    ...options,
  };

  const isFormData =
    typeof FormData !== "undefined" && requestConfig.data instanceof FormData;
  const headers = AxiosHeaders.concat(
    {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept-Language": getLanguage(),
    },
    config.headers as HeaderSource,
    options?.headers as HeaderSource,
  );
  if (isFormData) {
    headers.delete("Content-Type");
  }

  const token = getAccessToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return AXIOS_INSTANCE({
    ...requestConfig,
    headers,
  }).then(({ data }) => data);
};
